import React from 'react';
import { Users, TrendingUp, Activity } from 'lucide-react';
import { Kpi, Section } from './_shared';

export default function OverviewTab({ summary, adoption }) {
  if (!summary) return <div className="text-slate-500 text-sm p-4">Loading…</div>;

  const stickTone = summary.stickiness_pct >= 30 ? 'good' : summary.stickiness_pct >= 15 ? 'warn' : 'bad';
  const seatTone = summary.seat_utilization_pct >= 60 ? 'good' : summary.seat_utilization_pct >= 30 ? 'warn' : 'bad';
  const maxTrend = Math.max(1, ...(summary.trend || []).map(t => t.events));

  return (
    <div className="space-y-6" data-testid="governance-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Daily Active" value={summary.dau} sub={`${summary.wau} weekly · ${summary.mau} monthly`} />
        <Kpi icon={TrendingUp} label="Stickiness" value={`${summary.stickiness_pct}%`} sub="DAU / MAU" tone={stickTone} />
        <Kpi icon={Users} label="Seat utilization" value={`${summary.seat_utilization_pct}%`} sub={`${summary.active_seats_30d} of ${summary.total_seats} seats`} tone={seatTone} />
        <Kpi icon={Activity} label="Events (30d)" value={Object.values(summary.by_event || {}).reduce((a, b) => a + b, 0).toLocaleString()} sub={`${Object.keys(summary.by_event || {}).length} event types`} />
      </div>

      <Section title="Activity trend (last 30 days)">
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4">
          {(summary.trend || []).length === 0 ? (
            <div className="text-sm text-slate-400 italic">No activity yet — start using the app and stats will appear here after the nightly rollup.</div>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {(summary.trend || []).slice(-30).map(t => (
                <div key={t.date} className="flex-1 group relative">
                  <div
                    className="w-full bg-slate-800 dark:bg-slate-300 rounded-t transition-colors group-hover:bg-emerald-500"
                    style={{ height: `${(t.events / maxTrend) * 100}%` }}
                    title={`${t.date}: ${t.events} events, ${t.users} users`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {adoption?.funnel && (
        <Section title="Adoption funnel">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4">
            <div className="space-y-2">
              {(adoption.funnel || []).map((f, i) => {
                const first = adoption.funnel[0]?.count || 1;
                const pct = Math.min(100, Math.round((f.count / first) * 100));
                return (
                  <div key={f.stage} className="flex items-center gap-3">
                    <div className="w-32 text-sm text-slate-600 dark:text-slate-400">{f.stage}</div>
                    <div className="flex-1 relative">
                      <div className="h-6 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div className="h-full bg-slate-900 dark:bg-slate-100" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="w-16 text-right text-sm tabular-nums text-slate-700 dark:text-slate-300">{f.count}</div>
                    {i > 0 && <div className="w-12 text-right text-xs text-slate-400">{pct}%</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
